import { Ref, watch } from "vue";

// Type accepté pour la ref : `useNumber()` retourne `Ref<number>` mais
// `b-numberinput` peut y écrire `null` quand l'utilisateur efface le
// champ ; les composants appelants ( InputNumber , InputNumberInterval )
// utilisent un mix de typages . On accepte `Ref<unknown>` ici pour ne
// pas imposer un seul typage cible et garder le composable réutilisable .
type NumericRef = Ref<number> | Ref<number | null> | Ref<number | null | undefined> | Ref<number | undefined>;

/**
 * Corrige le comportement Buefy `b-numberinput` quand la valeur courante
 * est `null` / `undefined` : un clic + ou - saute directement aux bornes
 * ( min ou max ) plutôt que de stepper depuis 0 . Pour un IntegerChecker
 * sans borne explicite , min vaut -Infinity et max +Infinity ; pour un
 * IntegerChecker contraint , min / max peuvent valoir Integer.MIN_VALUE
 * ( -2147483648 ) ou Integer.MAX_VALUE ( 2147483647 ) . Dans les deux cas
 * le saut est inattendu .
 *
 * Comportement attendu côté utilisateur : premier clic + ou - sur un
 * champ vide pose 0 , les clics suivants stepent normalement
 * ( 0 -> 1 -> 2 ... ou 0 -> -1 -> -2 ... ) .
 *
 * @param refValue ref tenant la valeur du `b-numberinput` ( null si vide )
 * @param getMin   accessor réactif de la borne min courante
 * @param getMax   accessor réactif de la borne max courante
 * @param onEmpty  valeur posée au premier clic +/- ( défaut 0 )
 */
export default function useEmptyNumberJumpClamp(
  refValue: NumericRef,
  getMin: () => number,
  getMax: () => number,
  onEmpty: number = 0,
): void {
  watch(refValue, (newVal, oldVal) => {
    const wasEmpty = oldVal === null || oldVal === undefined;
    if (!wasEmpty) return;
    const min = getMin();
    const max = getMax();
    const hitBound = newVal === min
      || newVal === max
      || newVal === Number.NEGATIVE_INFINITY
      || newVal === Number.POSITIVE_INFINITY
      || (typeof newVal === "number" && !Number.isFinite(newVal));
    if (hitBound) (refValue as Ref<number>).value = onEmpty;
  });
}
